// Phase 6.0 STEP B · 판매자 숏클립 등록/관리
// Prod-S3 · 다중 상품 연결 (Multi-Product Clip) · 최대 10개
// Prod-S2.1 · pending_link 인라인 상품 연결 + 자동수집 알림 + 배지
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, X, Trash2, Play, Star, RefreshCw, Youtube, Music, Instagram, Link2, AlertCircle, CheckCircle2, Bell, ArrowUp, ArrowDown, Package, ExternalLink, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import ImageUpload from '@/components/ImageUpload';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// STEP SF-MULTIPOST-P2 · 플랫폼별 게시 상태 pill + retry 버튼 · 최소 확장.
// 기존 publish history endpoint(`/api/seller/short-clips/{cid}/publish-jobs`)만 재사용 · 실 SNS API 미호출.
const PUBLISH_STATUS_STYLES = {
  pending:    { label: 'PENDING',    bg: 'bg-gray-400',   text: 'text-white' },
  publishing: { label: 'PUBLISHING', bg: 'bg-blue-500',   text: 'text-white' },
  success:    { label: 'SUCCESS',    bg: 'bg-emerald-500',text: 'text-white' },
  failed:     { label: 'FAILED',     bg: 'bg-red-500',    text: 'text-white' },
  blocked:    { label: 'BLOCKED',    bg: 'bg-amber-500',  text: 'text-white' },
};

// SHORT-FORM MGMT UX · 플랫폼별 상태 pill 앞에 브랜드 아이콘을 통일 표시.
// 기존 lucide 아이콘 REUSE (Youtube · Instagram · Music) — 새 자원 추가 없음.
function PublishPlatformIcon({ platform, size = 10 }) {
  if (platform === 'youtube')   return <Youtube   size={size}/>;
  if (platform === 'instagram') return <Instagram size={size}/>;
  if (platform === 'tiktok')    return <Music     size={size}/>;
  return null;
}
// STEP 4-A BUGFIX · 직접 업로드된 영상은 video_url 이
// `/api/seller/short-clip-media/{media_id}` 을 가리키고 backend 가 판매자 auth 를
// 요구함. HTML `<video src>` 태그는 Authorization: Bearer 헤더를 자동으로 실을 수
// 없어 401 → 카드가 시각적으로 비어 보임(사용자가 "카드가 안 보인다"고 신고한 원인).
// 해결: axios 로 blob 을 받아 Object URL 로 <video> 에 주입 · 새 API/스키마 없음.
function DirectUploadVideoPreview({ videoUrl }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!videoUrl) return undefined;
    let mounted = true;
    let created = null;
    (async () => {
      try {
        // Strip backend origin — api 인스턴스가 REACT_APP_BACKEND_URL 을 baseURL 로
        // 가지므로 상대 경로만 넘겨야 Authorization 헤더가 실린다.
        const rel = videoUrl.replace(/^https?:\/\/[^/]+/, '');
        const r = await api.get(rel, { responseType: 'blob' });
        if (!mounted) return;
        const u = URL.createObjectURL(r.data);
        created = u;
        setBlobUrl(u);
      } catch {
        /* 401/404 등: silent · 카드 gray placeholder 유지 */
      }
    })();
    return () => { mounted = false; if (created) URL.revokeObjectURL(created); };
  }, [videoUrl]);
  if (!blobUrl) return null;
  return (
    <video src={blobUrl}
      className="absolute inset-0 w-full h-full object-cover"
      muted playsInline preload="metadata" controls
      onLoadedMetadata={(e) => { try { e.currentTarget.currentTime = 0.1; } catch {} }}
      data-testid="clip-inline-video"/>
  );
}

const PUBLISH_PLATFORM_LABEL = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok' };
const PUBLISH_STATUS_LABELS_KR = {
  pending:    '게시 대기',
  publishing: '게시 중',
  success:    '게시 완료',
  failed:     '게시 실패',
  blocked:    '게시 불가',
};

// SHORT-FORM MGMT UX · Outbound 게시상태 표시.
// - 기존 publish_jobs 데이터만 REUSE · 신규 API/스키마 없음.
// - 자동수집 클립(source_type=auto_scan)에는 절대 렌더하지 않음.
// - 부모(SellerShortClips)에서 미리 fetch 한 publishJob 을 prop 으로 받아 이중 fetch 방지.
//   ※ 없으면 backward-compat 로 자체 fetch 도 지원.
// - 플랫폼별 pill 앞에 브랜드 아이콘(Youtube/Instagram/Music) 표시 · 통일.
// - success + external URL(YouTube 는 external_content_id, IG 는 external_url) 링크 표시.
// - failed / blocked 은 [재시도] 버튼 + 짧은 실패 사유.
function PublishStatusPills({ clipId, sourceType, job: jobProp, onRetry }) {
  const [job, setJob] = useState(jobProp ?? null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(jobProp !== undefined);

  const load = useCallback(async () => {
    if (sourceType === 'auto_scan') { setLoaded(true); return; }
    if (jobProp !== undefined) { setJob(jobProp || null); setLoaded(true); return; }
    try {
      const r = await api.get(`/seller/short-clips/${clipId}/publish-jobs`);
      const items = r.data?.items || [];
      setJob(items[0] || null);
    } catch { setJob(null); }
    finally { setLoaded(true); }
  }, [clipId, sourceType, jobProp]);

  useEffect(() => { load(); }, [load]);
  // jobProp 이 부모에서 갱신되면 반영.
  useEffect(() => { if (jobProp !== undefined) setJob(jobProp || null); }, [jobProp]);

  const retry = async (platform) => {
    if (!job?.job_id) return;
    setBusy(true);
    try {
      await api.post(`/seller/publish-jobs/${job.job_id}/retry`, { platforms: [platform] });
      toast.success(`${PUBLISH_PLATFORM_LABEL[platform] || platform} 재시도 대기열에 등록됨`);
      if (onRetry) await onRetry(); else await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '재시도 실패');
    } finally { setBusy(false); }
  };

  // 자동수집 카드: 절대 표시하지 않음 (기존 출처 badge 만 유지).
  if (sourceType === 'auto_scan') return null;
  // 아직 로딩 전 / 게시요청 없는 clip: 카드 정돈을 위해 조용히 미노출.
  if (!loaded || !job) return null;

  const platforms = Object.entries(job.platforms || {});
  if (platforms.length === 0) return null;

  return (
    <div className="mt-1 space-y-1" data-testid={`clip-publish-status-${clipId}`}>
      {platforms.map(([platform, state]) => {
        const st = String(state?.status || 'pending').toLowerCase();
        const style = PUBLISH_STATUS_STYLES[st] || PUBLISH_STATUS_STYLES.pending;
        const label = PUBLISH_STATUS_LABELS_KR[st] || st.toUpperCase();
        const ext = state?.external_content_id || '';
        const extUrl = state?.external_url || '';
        const ytUrl = st === 'success' && platform === 'youtube' && ext
          ? `https://youtu.be/${ext}` : null;
        const igUrl = st === 'success' && platform === 'instagram' && extUrl
          ? extUrl : null;
        const errMsg = String(state?.error_message || '');
        const shortErr = errMsg.length > 40 ? errMsg.slice(0, 40) + '…' : errMsg;
        return (
          <div key={platform} className="flex flex-wrap items-center gap-1"
            data-testid={`clip-publish-row-${clipId}-${platform}`}>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black ${style.bg} ${style.text}`}
              title={errMsg}
              data-testid={`clip-publish-pill-${clipId}-${platform}`}>
              <PublishPlatformIcon platform={platform} size={10}/>
              {PUBLISH_PLATFORM_LABEL[platform] || platform} · {label}
            </span>
            {ytUrl && (
              <a href={ytUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-black border border-red-300 text-red-600 hover:bg-red-50"
                data-testid={`clip-publish-view-${clipId}-${platform}`}>
                <ExternalLink size={9}/> YouTube에서 보기
              </a>
            )}
            {igUrl && (
              <a href={igUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-black border border-fuchsia-300 text-fuchsia-600 hover:bg-fuchsia-50"
                data-testid={`clip-publish-view-${clipId}-${platform}`}>
                <ExternalLink size={9}/> Instagram에서 보기
              </a>
            )}
            {(st === 'failed' || st === 'blocked') && (
              <button onClick={() => retry(platform)} disabled={busy}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-black border border-gray-300 hover:border-[#FF3B30] hover:text-[#FF3B30] disabled:opacity-40"
                data-testid={`clip-publish-retry-${clipId}-${platform}`}
                aria-label={`${platform} 재시도`}>
                <RefreshCw size={9}/> 재시도
              </button>
            )}
            {shortErr && (st === 'failed' || st === 'blocked') && (
              <span className="text-[9px] text-gray-500 truncate max-w-[60%]"
                data-testid={`clip-publish-errmsg-${clipId}-${platform}`}>
                {shortErr}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Prod-S3 · Backend MAX_LINKED_PRODUCTS 와 동기화
const MAX_LINKED_PRODUCTS = 10;

// ---------------------------------------------------------------------------
// Prod-S3 · 다중 상품 선택 chip UI · ClipModal / PendingLinkCard 공용
// - Backend 검증(최대 10개 · 소유권 · 중복)과 병행 · 프런트 UX 방어
// ---------------------------------------------------------------------------
function MultiProductPicker({ products, value, onChange, disabled, testIdPrefix = 'mpp' }) {
  const [pick, setPick] = useState('');
  const selected = Array.isArray(value) ? value : [];
  const available = products.filter((p) => !selected.includes(p.product_id));
  const productMap = useMemo(() => {
    const m = {}; products.forEach((p) => { m[p.product_id] = p; }); return m;
  }, [products]);

  const add = (pid) => {
    if (!pid) return;
    if (selected.includes(pid)) return;
    if (selected.length >= MAX_LINKED_PRODUCTS) {
      toast.error(`상품은 최대 ${MAX_LINKED_PRODUCTS}개까지 연결할 수 있습니다.`);
      return;
    }
    onChange([...selected, pid]);
    setPick('');
  };
  const remove = (pid) => onChange(selected.filter((x) => x !== pid));
  const move = (idx, dir) => {
    const next = [...selected];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      <div className="flex items-center gap-2">
        <select value={pick}
          onChange={(e) => add(e.target.value)}
          disabled={disabled || selected.length >= MAX_LINKED_PRODUCTS || available.length === 0}
          className="flex-1 px-3 py-2 rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-xs disabled:opacity-50"
          data-testid={`${testIdPrefix}-select`}>
          <option value="">
            {available.length === 0
              ? (selected.length === 0 ? '등록된 상품이 없습니다' : '모두 선택됨')
              : '상품 추가 …'}
          </option>
          {available.map((p) => (
            <option key={p.product_id} value={p.product_id}>{p.name}</option>
          ))}
        </select>
        <span className="text-[11px] font-black text-gray-500 whitespace-nowrap"
          data-testid={`${testIdPrefix}-count`}>
          {selected.length}/{MAX_LINKED_PRODUCTS}
        </span>
      </div>

      {selected.length === 0 ? (
        <p className="text-[11px] text-gray-400 py-2">상품 연결은 선택사항입니다. 필요한 경우 상품을 선택해주세요.</p>
      ) : (
        <ul className="space-y-1.5" data-testid={`${testIdPrefix}-list`}>
          {selected.map((pid, i) => {
            const p = productMap[pid];
            return (
              <li key={pid}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f]"
                data-testid={`${testIdPrefix}-item-${pid}`}>
                {i === 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-[#FF3B30] text-white flex-shrink-0"
                    data-testid={`${testIdPrefix}-primary-badge`}>대표</span>
                )}
                <span className="text-xs font-bold flex-1 truncate">{p?.name || pid}</span>
                {p?.sale_price != null && (
                  <span className="text-[10px] text-gray-500 whitespace-nowrap">{won(p.sale_price)}</span>
                )}
                <button type="button" onClick={() => move(i, -1)} disabled={disabled || i === 0}
                  className="p-1 rounded hover:bg-black/5 disabled:opacity-20"
                  data-testid={`${testIdPrefix}-up-${pid}`} aria-label="위로">
                  <ArrowUp size={11}/>
                </button>
                <button type="button" onClick={() => move(i, 1)} disabled={disabled || i === selected.length - 1}
                  className="p-1 rounded hover:bg-black/5 disabled:opacity-20"
                  data-testid={`${testIdPrefix}-down-${pid}`} aria-label="아래로">
                  <ArrowDown size={11}/>
                </button>
                <button type="button" onClick={() => remove(pid)} disabled={disabled}
                  className="p-1 rounded text-red-500 hover:bg-red-50 disabled:opacity-40"
                  data-testid={`${testIdPrefix}-remove-${pid}`} aria-label="제거">
                  <X size={11}/>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {selected.length > 1 && (
        <p className="text-[10px] text-gray-500">첫 번째 상품이 대표(썸네일 하단 정보)로 노출됩니다. 순서를 조절할 수 있습니다.</p>
      )}
    </div>
  );
}


function ClipModal({ open, onClose, onSaved, editing }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    linked_product_ids: [], title: '', description: '',
    video_url: '', thumbnail_url: '',
  });
  const [busy, setBusy] = useState(false);
  // STEP 4-A · 등록 방식 · 기본 direct upload · YouTube URL Import 는 명확히 분리.
  const [mode, setMode] = useState('upload');
  // STEP 4-A · 직접 업로드된 미디어 · null 이면 아직 업로드 안 됨.
  //   { media_id, url, filename, size } — POST /seller/short-clip-media 응답.
  const [mediaUpload, setMediaUpload] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  // Prod-S1 · YouTube URL Import (기존 REUSE)
  const [ytUrl, setYtUrl] = useState('');
  const [ytBusy, setYtBusy] = useState(false);
  // STEP SF-MULTIPOST-P1 · Multi-platform publish
  const [publishTargets, setPublishTargets] = useState({ youtube: false, instagram: false, tiktok: false });
  const [publishCaps, setPublishCaps] = useState(null);
  // STEP SF-MULTIPOST-P3 · YouTube-only publish options.
  const [ytPrivacy, setYtPrivacy] = useState('private');
  const [ytMadeForKids, setYtMadeForKids] = useState(null);

  // STEP SF-MULTIPOST-P5 · TikTok-only publish options (driven by creator_info).
  const [ttCreatorInfo, setTtCreatorInfo] = useState(null);
  const [ttCreatorLoading, setTtCreatorLoading] = useState(false);
  const [ttPrivacy, setTtPrivacy] = useState('');
  const [ttDisableComment, setTtDisableComment] = useState(false);
  const [ttDisableDuet, setTtDisableDuet] = useState(false);
  const [ttDisableStitch, setTtDisableStitch] = useState(false);
  const [ttAckDisclosure, setTtAckDisclosure] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/seller/products').then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setProducts(arr);
    }).catch(() => setProducts([]));
    const initialLinked = editing
      ? (Array.isArray(editing.linked_products) && editing.linked_products.length > 0
          ? editing.linked_products.map((lp) => lp.product_id).filter(Boolean)
          : (editing.product_id ? [editing.product_id] : []))
      : [];
    setForm(editing ? {
      linked_product_ids: initialLinked,
      title: editing.title || '',
      description: editing.description || '',
      video_url: editing.video_url || '',
      thumbnail_url: editing.thumbnail_url || '',
    } : { linked_product_ids: [], title: '', description: '', video_url: '', thumbnail_url: '' });
    setPublishTargets({ youtube: false, instagram: false, tiktok: false });
    api.get('/seller/short-clips/publish/capabilities')
      .then((r) => setPublishCaps(r.data || null))
      .catch(() => setPublishCaps(null));
    setYtUrl('');
    setMode('upload');
    setMediaUpload(null);
    setUploadBusy(false);
    setUploadProgress(0);
    setYtPrivacy('private');
    setYtMadeForKids(null);
    setTtCreatorInfo(null);
    setTtPrivacy('');
    setTtDisableComment(false);
    setTtDisableDuet(false);
    setTtDisableStitch(false);
    setTtAckDisclosure(false);
  }, [open, editing]);

  // STEP SF-MULTIPOST-P5 · TikTok 게시 옵션은 creator_info API 응답 기반.
  //   · 사용자가 TikTok 을 선택하는 순간 fetch (재선택 시 재사용 · 캐시).
  //   · privacy_level_options / disable_* / max_video_post_duration_sec 를 정확히 반영.
  useEffect(() => {
    if (!open || editing) return;
    if (!publishTargets.tiktok) return;
    if (ttCreatorInfo || ttCreatorLoading) return;
    (async () => {
      setTtCreatorLoading(true);
      try {
        const r = await api.get('/seller/tiktok/creator-info');
        setTtCreatorInfo(r.data || null);
        // Default privacy: SELF_ONLY 우선(가장 안전) · 없으면 첫 옵션.
        const opts = (r.data?.privacy_level_options) || [];
        const def = opts.includes('SELF_ONLY') ? 'SELF_ONLY' : (opts[0] || '');
        setTtPrivacy(def);
        // creator_info 가 disable=true 로 강제한 항목은 UI 도 강제 체크.
        if (r.data?.comment_disabled) setTtDisableComment(true);
        if (r.data?.duet_disabled)    setTtDisableDuet(true);
        if (r.data?.stitch_disabled)  setTtDisableStitch(true);
      } catch {
        setTtCreatorInfo({ status: 'failed', error_code: 'network_error' });
      } finally {
        setTtCreatorLoading(false);
      }
    })();
  }, [open, editing, publishTargets.tiktok, ttCreatorInfo, ttCreatorLoading]);

  if (!open) return null;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // STEP 4-A · 실 영상 파일 업로드 (기존 POST /seller/short-clip-media REUSE ONLY).
  const pickVideo = () => fileInputRef.current?.click();
  const handleVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadBusy(true); setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post('/seller/short-clip-media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => {
          if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100));
        },
        timeout: 5 * 60 * 1000,
      });
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const relUrl = r.data?.url || `/api/seller/short-clip-media/${r.data?.media_id}`;
      setMediaUpload({
        media_id: r.data?.media_id,
        url: `${backend}${relUrl}`,
        filename: f.name,
        size: r.data?.size,
        mime: r.data?.mime || f.type || '',
      });
      // 등록 payload 의 video_url · thumbnail(비어있으면 자동) 은 submit 시 조합.
      toast.success('영상 업로드 완료');
    } catch (err) {
      toast.error(err?.response?.data?.detail || '영상 업로드 실패');
    } finally {
      setUploadBusy(false);
      // 같은 파일 재선택 가능하도록 초기화.
      if (e.target) e.target.value = '';
    }
  };

  const importYoutube = async () => {
    if (!ytUrl.trim()) { toast.error('YouTube 영상 주소를 입력해주세요.'); return; }
    setYtBusy(true);
    try {
      // STEP 4-A · YouTube URL Import 도 상품 선택을 필수로 요구하지 않음 (기존 backend
      // 는 product_id 옵셔널 허용). 0개면 legacy 호환용 빈 문자열 전송.
      const primary = form.linked_product_ids[0] || '';
      const r = await api.post('/seller/short-clips/youtube/import-url', {
        url: ytUrl.trim(),
        product_id: primary,
      });
      if (r.data?.duplicate) {
        toast.warning(r.data?.detail || '이미 등록된 YouTube 영상입니다.');
      } else {
        const cid = r.data?.short_clip?.clip_id;
        if (cid && form.linked_product_ids.length > 1) {
          await api.patch(`/seller/short-clips/${cid}`, {
            linked_products: form.linked_product_ids,
          });
        }
        toast.success(form.linked_product_ids.length > 0
          ? `YouTube Shorts 등록 완료 · 상품 ${form.linked_product_ids.length}개 연결됨.`
          : 'YouTube Shorts 등록 완료');
      }
      onSaved?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'YouTube 가져오기에 실패했습니다.');
    } finally { setYtBusy(false); }
  };

  const submit = async () => {
    // STEP 4-A · Direct upload flow validation.
    // 신규 등록 시: 영상 업로드 필수 (mediaUpload 또는 기존 form.video_url).
    const chosen = Object.entries(publishTargets).filter(([, v]) => v).map(([k]) => k);
    const finalVideoUrl = mediaUpload?.url || form.video_url || '';
    const finalMediaId = mediaUpload?.media_id || null;
    if (!editing && !finalVideoUrl) {
      toast.error('동영상 파일을 업로드해주세요.'); return;
    }
    // Outbound (YouTube 등) 선택 시 source_media_id 필수.
    if (!editing && chosen.length > 0 && !finalMediaId) {
      toast.error('외부 게시를 위해 동영상 파일을 업로드해주세요.'); return;
    }
    // STEP SHORT-3WAY-2 · GAP-4 · 3사 공통 미디어 포맷 사전 검증.
    //   · TikTok 포함    → MP4 만 허용
    //   · Instagram 포함 → MP4 또는 MOV 만 허용
    //   · YouTube 단독   → 업로드 시 허용된 mime 그대로
    // 백엔드에서도 동일 검증을 수행하지만 UX 를 위해 게시 실행 전에 안내.
    const uploadedMime = String(mediaUpload?.mime || '').toLowerCase();
    if (!editing && uploadedMime && chosen.length > 0) {
      if (chosen.includes('tiktok') && uploadedMime !== 'video/mp4') {
        toast.error('TikTok 이 포함된 동시 게시에는 MP4 영상을 사용해 주세요.');
        return;
      }
      if (chosen.includes('instagram') &&
          !['video/mp4', 'video/quicktime'].includes(uploadedMime)) {
        toast.error('Instagram Reels 게시에는 MP4 또는 MOV 영상을 사용해 주세요.');
        return;
      }
    }
    if (chosen.includes('youtube') && ytMadeForKids === null) {
      toast.error('YouTube 게시 시 아동용 여부(madeForKids)를 명시 선택해주세요.'); return;
    }
    // STEP SF-MULTIPOST-P5 · TikTok 사전 검증 · creator_info 기반.
    if (chosen.includes('tiktok')) {
      if (ttCreatorInfo?.status !== 'ok') {
        toast.error('TikTok 계정 정보(creator_info) 를 불러오지 못했습니다. 계정 연결/권한을 확인해주세요.'); return;
      }
      const opts = ttCreatorInfo.privacy_level_options || [];
      if (!opts.includes(ttPrivacy)) {
        toast.error('TikTok 공개 범위를 선택해주세요.'); return;
      }
      if (!ttAckDisclosure) {
        toast.error('TikTok 게시 조건(공개 범위·인터랙션 설정) 을 확인했다고 표시해주세요.'); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        linked_products: form.linked_product_ids,
        title: form.title,
        description: form.description,
        video_url: finalVideoUrl,
        thumbnail_url: form.thumbnail_url,
      };
      if (finalMediaId) payload.source_media_id = finalMediaId;
      let createdClipId = editing?.clip_id;
      if (editing) await api.patch(`/seller/short-clips/${editing.clip_id}`, payload);
      else {
        const r = await api.post('/seller/short-clips', payload);
        createdClipId = r.data?.short_clip?.clip_id;
      }
      if (createdClipId && chosen.length > 0) {
        const publishBody = { platforms: chosen };
        if (chosen.includes('youtube')) {
          publishBody.youtube_options = {
            title: form.title || '',
            description: form.description || '',
            privacyStatus: ytPrivacy,
            madeForKids: ytMadeForKids,
          };
        }
        if (chosen.includes('tiktok')) {
          publishBody.tiktok_options = {
            title:            form.title || form.description || '',
            privacy_level:    ttPrivacy,
            disable_comment:  ttDisableComment,
            disable_duet:     ttDisableDuet,
            disable_stitch:   ttDisableStitch,
          };
        }
        try {
          await api.post(`/seller/short-clips/${createdClipId}/publish`, publishBody);
          const blockedList = chosen.filter((p) => !publishCaps?.platforms?.[p]?.feature_enabled);
          if (blockedList.length === chosen.length) {
            toast.info(`게시 예약: ${chosen.length}개 채널 · 승인 대기 상태 (실 게시 X)`);
          } else {
            toast.success(`게시 예약: ${chosen.length}개 채널`);
          }
        } catch (e) {
          toast.warning(`숏클립은 저장됐지만 게시 예약 실패: ${e?.response?.data?.detail || 'unknown'}`);
        }
      } else {
        toast.success(editing ? '수정 완료' : 'LIVEMOA 에 등록되었습니다.');
      }
      onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setBusy(false); }
  };

  const outboundBlockedNoMedia = !editing && Object.values(publishTargets).some(Boolean) && !mediaUpload;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="clip-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg h-[95vh] sm:h-auto sm:max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white dark:bg-[#141414] shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-black/5 dark:border-white/5 sticky top-0 bg-white dark:bg-[#141414] z-10">
          <h3 className="text-lg font-black">{editing ? '숏클립 수정' : '숏클립 등록'}</h3>
          <button onClick={onClose} className="p-2 -mr-2 hover:bg-black/5 rounded"><X size={18}/></button>
        </div>

        <div className="flex-1 p-4 sm:p-5 space-y-4 text-sm">
          {/* SHORT-FORM MGMT UX · 등록 방식 토글 (신규 등록에서만 노출) */}
          {!editing && (
            <div className="flex gap-1.5 p-1 rounded-full bg-black/5 dark:bg-white/10" data-testid="clip-mode-toggle">
              <button onClick={() => setMode('upload')}
                className={`flex-1 py-2.5 rounded-full text-xs font-black transition ${mode === 'upload' ? 'bg-white dark:bg-[#141414] shadow text-black dark:text-white' : 'text-gray-500'}`}
                data-testid="clip-mode-upload">직접 업로드</button>
              <button onClick={() => setMode('yt_import')}
                className={`flex-1 py-2.5 rounded-full text-xs font-black transition ${mode === 'yt_import' ? 'bg-white dark:bg-[#141414] shadow text-black dark:text-white' : 'text-gray-500'}`}
                data-testid="clip-mode-yt">SNS 콘텐츠 가져오기</button>
            </div>
          )}

          {/* -------- Mode: SNS 콘텐츠 가져오기 (YouTube URL Import + IG/TT 자동수집 안내) -------- */}
          {!editing && mode === 'yt_import' && (
            <div className="space-y-3" data-testid="clip-sns-import-section">
              {/* YouTube: 기존 URL Import REUSE */}
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30" data-testid="clip-yt-import-section">
                <p className="text-[11px] font-black text-red-700 dark:text-red-300 mb-2 inline-flex items-center gap-1">
                  <Youtube size={12}/> YouTube Shorts URL 로 가져오기
                </p>
                <div className="space-y-2">
                  <input value={ytUrl} onChange={(e) => setYtUrl(e.target.value)}
                    placeholder="https://youtube.com/shorts/..."
                    className="w-full px-3 py-3 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-sm"
                    data-testid="clip-yt-url"/>
                  <div>
                    <p className="text-xs font-black text-gray-700 dark:text-gray-200 mb-1.5">
                      연결 상품 (선택)
                      <span className="text-[10px] font-normal text-gray-400 ml-1">최대 {MAX_LINKED_PRODUCTS}개</span>
                    </p>
                    <MultiProductPicker products={products}
                      value={form.linked_product_ids}
                      onChange={(v) => setField('linked_product_ids', v)}
                      testIdPrefix="clip-yt-mpp"/>
                  </div>
                  <button onClick={importYoutube} disabled={ytBusy || !ytUrl.trim()}
                    className="w-full px-3 py-3 rounded-lg text-sm font-black bg-red-600 text-white disabled:opacity-40"
                    data-testid="clip-yt-import">
                    {ytBusy ? '가져오는 중…' : 'YouTube 에서 가져오기'}
                  </button>
                  <p className="text-[10px] text-gray-500 leading-relaxed">
                    · 연결된 YouTube 계정의 공개 영상만 가져올 수 있습니다.<br/>
                    · 영상은 다운로드되지 않고 YouTube 원본에서 재생됩니다.<br/>
                    · 이 방식은 <b>다른 SNS 동시게시 대상이 아닙니다.</b>
                  </p>
                </div>
              </div>

              {/* Instagram · TikTok: 자동수집 안내 (신규 URL Import 없음) */}
              <div className="p-3 rounded-xl bg-fuchsia-50 dark:bg-fuchsia-500/10 border border-fuchsia-200 dark:border-fuchsia-500/30" data-testid="clip-ig-autoscan-info">
                <p className="text-[11px] font-black text-fuchsia-700 dark:text-fuchsia-300 mb-1 inline-flex items-center gap-1">
                  <Instagram size={12}/> Instagram Reels
                </p>
                <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed">
                  연결된 Instagram 계정의 Reels 는 <b>자동으로 수집</b>됩니다.
                  이 창을 닫고 위 <b>쇼츠 자동 스캔</b> 배너에서 [지금 스캔] 을 눌러주세요.
                </p>
              </div>
              <div className="p-3 rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10" data-testid="clip-tt-autoscan-info">
                <p className="text-[11px] font-black text-gray-800 dark:text-gray-200 mb-1 inline-flex items-center gap-1">
                  <Music size={12}/> TikTok
                </p>
                <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed">
                  연결된 TikTok 계정의 영상은 <b>자동으로 수집</b>됩니다.
                  이 창을 닫고 위 <b>쇼츠 자동 스캔</b> 배너에서 [지금 스캔] 을 눌러주세요.
                </p>
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed">
                ※ Instagram 과 TikTok 은 연결된 계정에서 자동으로 가져옵니다. 별도의 URL 붙여넣기 방식은 제공하지 않습니다.
              </p>
            </div>
          )}

          {/* -------- Mode: Direct Upload -------- */}
          {(editing || mode === 'upload') && (
            <>
              {/* 1. 동영상 (upload) */}
              <div>
                <label className="text-xs font-black text-gray-700 dark:text-gray-200 mb-1.5 block">
                  1. 동영상 {!editing && <span className="text-red-500">*</span>}
                </label>
                <input ref={fileInputRef} type="file" accept="video/*"
                  onChange={handleVideoFile} className="hidden"
                  data-testid="clip-video-file-input"/>
                {!mediaUpload && !editing ? (
                  <button onClick={pickVideo} disabled={uploadBusy}
                    className="w-full py-6 rounded-xl border-2 border-dashed border-black/15 dark:border-white/15 hover:border-[#FF3B30] disabled:opacity-60 flex flex-col items-center justify-center gap-1"
                    data-testid="clip-video-pick">
                    <Play size={20}/>
                    <span className="text-xs font-bold">
                      {uploadBusy ? `업로드 중… ${uploadProgress}%` : '동영상 파일 선택'}
                    </span>
                    {uploadBusy && (
                      <div className="w-2/3 h-1 rounded-full bg-black/10 overflow-hidden mt-1">
                        <div className="h-full bg-[#FF3B30] transition-all"
                          style={{ width: `${uploadProgress}%` }}/>
                      </div>
                    )}
                    {!uploadBusy && <span className="text-[10px] text-gray-400">MP4 · 최대 300MB</span>}
                  </button>
                ) : mediaUpload ? (
                  <div className="p-3 rounded-xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/10"
                    data-testid="clip-video-uploaded">
                    <p className="text-xs font-black text-emerald-700 dark:text-emerald-300 mb-1">
                      <CheckCircle2 size={12} className="inline mr-1"/> 업로드 완료
                    </p>
                    <p className="text-[11px] text-gray-600 dark:text-gray-300 truncate">
                      {mediaUpload.filename}
                      {mediaUpload.size ? ` · ${(mediaUpload.size / (1024 * 1024)).toFixed(1)}MB` : ''}
                    </p>
                    <button onClick={pickVideo} disabled={uploadBusy}
                      className="mt-2 text-[11px] font-bold underline text-gray-600 dark:text-gray-300"
                      data-testid="clip-video-change">
                      {uploadBusy ? `업로드 중… ${uploadProgress}%` : '영상 변경'}
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-500">기존 영상 유지</p>
                )}
              </div>

              {/* 2. 제목 */}
              <div>
                <label className="text-xs font-black text-gray-700 dark:text-gray-200 mb-1.5 block">2. 제목</label>
                <input value={form.title} onChange={(e) => setField('title', e.target.value)}
                  placeholder="예: 봄맞이 뷰티 하이라이트"
                  className="w-full px-3 py-3 rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
                  data-testid="clip-title"/>
              </div>

              {/* 3. 설명 */}
              <div>
                <label className="text-xs font-black text-gray-700 dark:text-gray-200 mb-1.5 block">3. 설명 (선택)</label>
                <textarea value={form.description} onChange={(e) => setField('description', e.target.value)}
                  rows={3} placeholder="영상 설명을 입력해주세요"
                  className="w-full px-3 py-3 rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
                  data-testid="clip-desc"/>
              </div>

              {/* 4. 연결 상품 (선택) */}
              <div>
                <label className="text-xs font-black text-gray-700 dark:text-gray-200 mb-1.5 block">
                  4. 연결 상품 (선택)
                  <span className="text-[10px] font-normal text-gray-400 ml-1">
                    {form.linked_product_ids.length}/{MAX_LINKED_PRODUCTS}
                  </span>
                </label>
                <MultiProductPicker products={products}
                  value={form.linked_product_ids}
                  onChange={(v) => setField('linked_product_ids', v)}
                  testIdPrefix="clip-modal-mpp"/>
              </div>

              {/* 5. 썸네일 */}
              <div>
                <label className="text-xs font-black text-gray-700 dark:text-gray-200 mb-1.5 block">
                  5. 썸네일 이미지 (선택)
                </label>
                <ImageUpload value={form.thumbnail_url}
                  onChange={(url) => setField('thumbnail_url', url)}
                  kind="broadcast"
                  aspectClass="aspect-[9/16] max-w-[140px]"
                  testId="clip-thumb-upload"/>
              </div>

              {/* 6. 게시 채널 */}
              {!editing && publishCaps && (
                <div className="p-3 rounded-xl border border-black/10 dark:border-white/10"
                  data-testid="clip-publish-channels-section">
                  <label className="text-xs font-black text-gray-700 dark:text-gray-200 mb-2 block">
                    6. 게시 채널
                    <span className="text-[10px] font-normal text-gray-400 ml-1">(승인 후 자동 게시)</span>
                  </label>
                  <div className="space-y-1.5">
                    {[{k:'livemoa',lb:'LIVEMOA (기본)'},{k:'youtube',lb:'YouTube Shorts'},{k:'instagram',lb:'Instagram Reels'},{k:'tiktok',lb:'TikTok'}].map(({k,lb}) => {
                      if (k === 'livemoa') {
                        return (
                          <div key={k} className="flex items-center gap-2 py-2.5 px-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10">
                            <CheckCircle2 size={14} className="text-emerald-600"/>
                            <span className="flex-1 text-xs font-bold">{lb}</span>
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700">항상 등록</span>
                          </div>
                        );
                      }
                      const cap = publishCaps.platforms?.[k] || {};
                      const disabled = !cap.oauth_connected || !cap.feature_enabled;
                      const badge = !cap.oauth_connected ? '계정 미연결' : !cap.feature_enabled ? '승인 대기' : '준비 완료';
                      const badgeCls = !cap.oauth_connected ? 'bg-gray-500/10 text-gray-500' : !cap.feature_enabled ? 'bg-amber-500/10 text-amber-700' : 'bg-emerald-500/10 text-emerald-700';
                      const active = publishTargets[k];
                      return (
                        <label key={k}
                          className={`flex items-center gap-2 py-2.5 px-2 rounded-lg border ${active ? 'border-[#FF3B30] bg-red-50/50 dark:bg-red-500/10' : 'border-transparent hover:bg-black/5'} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`clip-publish-target-${k}`}>
                          <input type="checkbox" checked={active} disabled={disabled}
                            onChange={(e) => setPublishTargets((prev) => ({ ...prev, [k]: e.target.checked }))}
                            className="w-4 h-4"
                            data-testid={`clip-publish-target-${k}-checkbox`}/>
                          <span className="flex-1 text-xs font-bold">{lb}</span>
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${badgeCls}`}>{badge}</span>
                        </label>
                      );
                    })}
                  </div>
                  {outboundBlockedNoMedia && (
                    <p className="mt-2 text-[10px] font-bold text-red-500" data-testid="clip-outbound-need-media">
                      ※ 외부 게시를 위해 동영상 파일을 업로드해주세요.
                    </p>
                  )}
                  <p className="mt-2 text-[10px] text-gray-400 leading-relaxed">
                    ※ 각 SNS 공식 심사 통과 후 자동으로 실 게시됩니다.
                  </p>

                  {/* 7. YouTube 게시 옵션 (YouTube 선택 시에만 노출) */}
                  {publishTargets.youtube && (
                    <div className="mt-3 pt-3 border-t border-black/10 dark:border-white/10 space-y-3"
                      data-testid="clip-yt-options-section">
                      <p className="text-[11px] font-black text-red-700 dark:text-red-300">
                        🎬 YouTube 게시 옵션
                      </p>
                      <div>
                        <label className="text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1 block">
                          공개 범위 <span className="text-red-500">*</span>
                        </label>
                        <select value={ytPrivacy} onChange={(e) => setYtPrivacy(e.target.value)}
                          className="w-full px-3 py-3 rounded-lg border border-black/10 dark:border-white/10 bg-transparent text-sm"
                          data-testid="clip-yt-privacy-select">
                          <option value="private">비공개 (Audit 전 안전 · 권장)</option>
                          <option value="unlisted">일부공개 (링크 있는 사람만)</option>
                          <option value="public">공개 (Google Audit 승인 후 실효)</option>
                        </select>
                        <p className="mt-1 text-[9px] text-gray-400">
                          ※ Google Audit 미완료 시 public 설정도 YouTube가 강제 private 처리합니다.
                        </p>
                      </div>
                      <div>
                        <label className="text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1 block">
                          아동용 콘텐츠 여부 (COPPA) <span className="text-red-500">*</span>
                        </label>
                        <div className="flex flex-col gap-1.5">
                          <label className="flex items-center gap-2 py-2 px-2 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer">
                            <input type="radio" name="clip-yt-mfk" checked={ytMadeForKids === false}
                              onChange={() => setYtMadeForKids(false)} data-testid="clip-yt-mfk-no"/>
                            <span className="text-xs font-bold">아동용 아님</span>
                          </label>
                          <label className="flex items-center gap-2 py-2 px-2 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer">
                            <input type="radio" name="clip-yt-mfk" checked={ytMadeForKids === true}
                              onChange={() => setYtMadeForKids(true)} data-testid="clip-yt-mfk-yes"/>
                            <span className="text-xs font-bold">아동용 콘텐츠</span>
                          </label>
                        </div>
                        {ytMadeForKids === null && (
                          <p className="mt-1 text-[10px] text-red-500 font-bold">
                            ※ YouTube 정책상 판매자가 명시적으로 선택해야 게시가 가능합니다.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP SF-MULTIPOST-P5 · TikTok 게시 옵션 (TikTok 선택 시에만 노출) */}
                  {publishTargets.tiktok && (
                    <div className="mt-3 pt-3 border-t border-black/10 dark:border-white/10 space-y-3"
                      data-testid="clip-tt-options-section">
                      <p className="text-[11px] font-black text-gray-900 dark:text-white inline-flex items-center gap-1">
                        <Music size={12}/> TikTok Direct Post 옵션
                      </p>

                      {ttCreatorLoading && (
                        <p className="text-[11px] text-gray-500" data-testid="clip-tt-loading">
                          TikTok 계정 정보를 불러오는 중…
                        </p>
                      )}
                      {!ttCreatorLoading && ttCreatorInfo?.status !== 'ok' && (
                        <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300 text-[11px] text-amber-700 dark:text-amber-300"
                          data-testid="clip-tt-creator-error">
                          TikTok 계정 정보를 가져오지 못했습니다.
                          {ttCreatorInfo?.error_code === 'scope_missing'
                            ? ' · video.publish 권한이 없어 재연결이 필요합니다.'
                            : ttCreatorInfo?.error_code === 'oauth_required'
                              ? ' · TikTok 계정을 먼저 연결해주세요.'
                              : ` · ${ttCreatorInfo?.error_message || ttCreatorInfo?.error_code || 'unknown'}`}
                        </div>
                      )}

                      {ttCreatorInfo?.status === 'ok' && (
                        <>
                          <div className="flex items-center gap-2 text-[11px]">
                            {ttCreatorInfo.creator_avatar_url && (
                              <img src={ttCreatorInfo.creator_avatar_url} alt=""
                                className="w-6 h-6 rounded-full object-cover"/>
                            )}
                            <span className="font-black text-gray-700 dark:text-gray-200"
                              data-testid="clip-tt-creator-name">
                              @{ttCreatorInfo.creator_username || 'unknown'}
                            </span>
                            {ttCreatorInfo.max_video_post_duration_sec ? (
                              <span className="text-gray-500">
                                · 최대 {ttCreatorInfo.max_video_post_duration_sec}초
                              </span>
                            ) : null}
                          </div>

                          <div>
                            <label className="text-[11px] font-bold text-gray-600 dark:text-gray-300 mb-1 block">
                              공개 범위 <span className="text-red-500">*</span>
                            </label>
                            <select value={ttPrivacy} onChange={(e) => setTtPrivacy(e.target.value)}
                              className="w-full px-3 py-3 rounded-lg border border-black/10 dark:border-white/10 bg-transparent text-sm"
                              data-testid="clip-tt-privacy-select">
                              <option value="">공개 범위 선택…</option>
                              {(ttCreatorInfo.privacy_level_options || []).map((v) => (
                                <option key={v} value={v}>
                                  {v === 'PUBLIC_TO_EVERYONE' ? '전체 공개 (Public to Everyone)'
                                    : v === 'MUTUAL_FOLLOW_FRIENDS' ? '맞팔 친구 (Mutual Follow Friends)'
                                    : v === 'FOLLOWER_OF_CREATOR' ? '팔로워 (Followers)'
                                    : v === 'SELF_ONLY' ? '나만 보기 (Self Only · 심사 전 권장)'
                                    : v}
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-[9px] text-gray-400">
                              ※ TikTok 이 계정별로 허용한 옵션만 표시됩니다.
                              심사 완료 전 unaudited 앱은 SELF_ONLY 만 실제 반영됩니다.
                            </p>
                          </div>

                          <div className="grid grid-cols-1 gap-1.5">
                            <label className="flex items-center gap-2 py-2 px-2 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer">
                              <input type="checkbox" checked={ttDisableComment}
                                disabled={ttCreatorInfo.comment_disabled}
                                onChange={(e) => setTtDisableComment(e.target.checked)}
                                data-testid="clip-tt-disable-comment"/>
                              <span className="text-xs font-bold">
                                댓글 비허용{ttCreatorInfo.comment_disabled ? ' (계정 강제)' : ''}
                              </span>
                            </label>
                            <label className="flex items-center gap-2 py-2 px-2 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer">
                              <input type="checkbox" checked={ttDisableDuet}
                                disabled={ttCreatorInfo.duet_disabled}
                                onChange={(e) => setTtDisableDuet(e.target.checked)}
                                data-testid="clip-tt-disable-duet"/>
                              <span className="text-xs font-bold">
                                듀엣 비허용{ttCreatorInfo.duet_disabled ? ' (계정 강제)' : ''}
                              </span>
                            </label>
                            <label className="flex items-center gap-2 py-2 px-2 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer">
                              <input type="checkbox" checked={ttDisableStitch}
                                disabled={ttCreatorInfo.stitch_disabled}
                                onChange={(e) => setTtDisableStitch(e.target.checked)}
                                data-testid="clip-tt-disable-stitch"/>
                              <span className="text-xs font-bold">
                                스티치 비허용{ttCreatorInfo.stitch_disabled ? ' (계정 강제)' : ''}
                              </span>
                            </label>
                          </div>

                          <label className="flex items-start gap-2 py-2 px-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300 cursor-pointer">
                            <input type="checkbox" checked={ttAckDisclosure}
                              onChange={(e) => setTtAckDisclosure(e.target.checked)}
                              className="mt-0.5"
                              data-testid="clip-tt-ack"/>
                            <span className="text-[11px] font-bold text-amber-800 dark:text-amber-200">
                              위 공개 범위·인터랙션 설정을 확인했으며,
                              TikTok Community Guidelines & Terms of Service 에 따라 게시함을 확인합니다.
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Sticky bottom actions */}
        {(editing || mode === 'upload') && (
          <div className="p-4 sm:p-5 border-t border-black/5 dark:border-white/5 flex gap-2 bg-white dark:bg-[#141414] sticky bottom-0">
            <button onClick={onClose}
              className="flex-1 sm:flex-none px-4 py-3 rounded-full text-sm font-bold border border-black/10"
              data-testid="clip-cancel">취소</button>
            <button onClick={submit} disabled={busy || uploadBusy}
              className="flex-1 px-4 py-3 rounded-full text-sm font-black bg-[#FF3B30] text-white disabled:opacity-50"
              data-testid="clip-submit">
              {busy ? '저장 중…' : (editing ? '수정'
                : (Object.values(publishTargets).some(Boolean) ? '등록 · 동시 게시' : 'LIVEMOA 에 등록'))}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prod-S2 · YouTube Shorts Auto-Scan 배너
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Prod-S · 쇼츠 자동 스캔 배너 (플랫폼 공통 · YouTube + TikTok)
// - UI 표시만 공통화. 각 플랫폼별 backend endpoint · 스캔 로직 무변경 재사용.
// ---------------------------------------------------------------------------
function PlatformScanRow({ platform, settings, onScanned, onToggle, onScanNow }) {
  const [busy, setBusy] = useState(false);
  const [toggling, setToggling] = useState(false);
  const meta = platform === 'youtube'
    ? { name: 'YouTube', tone: 'red', Icon: Youtube, chip: 'bg-red-600 text-white',
        border: 'border-red-200 dark:border-red-500/30', bg: 'bg-red-50 dark:bg-red-500/10',
        btn: 'bg-red-600 text-white', outline: 'border-red-600/50 text-red-700 dark:text-red-300',
        intervalLabel: '6시간마다' }
    : platform === 'tiktok'
    ? { name: 'TikTok',  tone: 'black', Icon: Music,   chip: 'bg-black text-white',
        border: 'border-black/20 dark:border-white/20', bg: 'bg-black/[0.03] dark:bg-white/[0.04]',
        btn: 'bg-black text-white dark:bg-white dark:text-black',
        outline: 'border-black/40 text-black dark:border-white/40 dark:text-white',
        intervalLabel: '12시간마다' }
    : { name: 'Instagram', tone: 'ig', Icon: Instagram,
        chip: 'bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white',
        border: 'border-fuchsia-300 dark:border-fuchsia-500/30',
        bg: 'bg-fuchsia-50 dark:bg-fuchsia-500/10',
        btn: 'bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white',
        outline: 'border-fuchsia-500/50 text-fuchsia-700 dark:text-fuchsia-300',
        intervalLabel: '12시간마다' };
  const Icon = meta.Icon;
  const doToggle = async () => { setToggling(true); try { await onToggle(!settings.enabled); } finally { setToggling(false); } };
  const doScan   = async () => { setBusy(true);     try { await onScanNow(); onScanned?.(); } finally { setBusy(false); } };
  const lastAt = settings.last_at || settings.last_tick?.at;
  const lastLabel = lastAt ? new Date(lastAt).toLocaleString('ko-KR') : '아직 없음';
  const label = settings.channel_title || settings.display_name;
  return (
    <div className={`p-3 rounded-xl border ${meta.border} ${meta.bg}`} data-testid={`${platform}-autoscan-row`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg ${meta.chip} flex items-center justify-center flex-shrink-0`}>
          <Icon size={18}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-black">{meta.name} 연결됨</p>
            {label && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/70 dark:bg-black/30 text-gray-700 dark:text-gray-200">
                {label}
              </span>
            )}
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-black ${settings.enabled ? 'bg-green-600 text-white' : 'bg-gray-400 text-white'}`}
              data-testid={`${platform}-autoscan-status-badge`}>
              {settings.enabled ? '자동 스캔 켜짐' : '자동 스캔 꺼짐'}
            </span>
          </div>
          <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-0.5">
            내 {meta.name} 계정의 신규 쇼츠를 {meta.intervalLabel} 자동으로 가져옵니다. 가져온 쇼츠는 <b>상품을 연결한 뒤</b> 소비자 피드에 노출됩니다.
          </p>
          <p className="text-[10px] text-gray-500 mt-1" data-testid={`${platform}-autoscan-last-at`}>
            마지막 스캔 · {lastLabel}
            {typeof settings.last_inserted === 'number' && ` · 추가 ${settings.last_inserted}개`}
          </p>
        </div>
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button onClick={doScan} disabled={busy}
          className={`px-3 py-1.5 rounded-full text-xs font-black inline-flex items-center gap-1 disabled:opacity-50 ${meta.btn}`}
          data-testid={`${platform}-autoscan-run-now`}>
          <RefreshCw size={12} className={busy ? 'animate-spin' : ''}/> {busy ? '스캔 중…' : '지금 스캔'}
        </button>
        <button onClick={doToggle} disabled={toggling}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border disabled:opacity-50 ${meta.outline}`}
          data-testid={`${platform}-autoscan-toggle`}>
          {toggling ? '변경 중…' : (settings.enabled ? '자동 스캔 끄기' : '자동 스캔 켜기')}
        </button>
      </div>
    </div>
  );
}

function ShortsAutoScanBanner({ onScanned }) {
  const [yt, setYt] = useState(null);
  const [tt, setTt] = useState(null);
  const [ig, setIg] = useState(null);

  const load = useCallback(async () => {
    const [ytR, ttR, igR] = await Promise.all([
      api.get('/seller/short-clips/youtube/auto-scan-settings').catch(() => null),
      api.get('/seller/short-clips/tiktok/auto-scan-settings').catch(() => null),
      api.get('/seller/short-clips/instagram/auto-scan-settings').catch(() => null),
    ]);
    setYt(ytR?.data || null);
    setTt(ttR?.data || null);
    setIg(igR?.data || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (yt === null && tt === null && ig === null) return null;

  const ytConnected = !!yt?.connected;
  const ttConnected = !!tt?.connected;
  const igConnected = !!ig?.connected;
  const anyConnected = ytConnected || ttConnected || igConnected;

  const toggleYt = async (enabled) => {
    try {
      const r = await api.patch('/seller/short-clips/youtube/auto-scan-settings', { enabled });
      toast.success(r.data?.enabled ? 'YouTube 자동 스캔이 켜졌습니다.' : 'YouTube 자동 스캔이 꺼졌습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '변경 실패'); }
  };
  const toggleTt = async (enabled) => {
    try {
      const r = await api.patch('/seller/short-clips/tiktok/auto-scan-settings', { enabled });
      toast.success(r.data?.enabled ? 'TikTok 자동 스캔이 켜졌습니다.' : 'TikTok 자동 스캔이 꺼졌습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '변경 실패'); }
  };
  const toggleIg = async (enabled) => {
    try {
      const r = await api.patch('/seller/short-clips/instagram/auto-scan-settings', { enabled });
      toast.success(r.data?.enabled ? 'Instagram 자동 스캔이 켜졌습니다.' : 'Instagram 자동 스캔이 꺼졌습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '변경 실패'); }
  };

  const scanYtNow = async () => {
    try {
      const r = await api.post('/seller/short-clips/youtube/scan');
      const d = r.data || {};
      if (d.status === 'scanned') {
        if (d.inserted > 0) toast.success(`YouTube 에서 새 쇼츠 ${d.inserted}개를 가져왔습니다. 상품을 연결해주세요.`);
        else toast.info('YouTube · 신규 쇼츠가 없습니다.');
      } else if (d.status === 'playlist_not_found') toast.warning('YouTube · 아직 이 채널에 Shorts 가 없거나 목록을 찾지 못했습니다.');
      else if (d.status === 'no_channel') toast.error('YouTube · 연결된 채널 정보를 확인할 수 없습니다.');
      else if (d.status === 'no_oauth')   toast.error('YouTube 계정을 먼저 연결해주세요.');
      else if (d.status === 'error')      toast.error(`YouTube 스캔 실패: ${d.error_code || 'unknown'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'YouTube 스캔 실패'); }
  };
  const scanTtNow = async () => {
    try {
      const r = await api.post('/seller/short-clips/tiktok/scan');
      const d = r.data || {};
      if (d.status === 'scanned') {
        if (d.inserted > 0) toast.success(`TikTok 에서 새 쇼츠 ${d.inserted}개를 가져왔습니다. 상품을 연결해주세요.`);
        else toast.info('TikTok · 신규 쇼츠가 없습니다.');
      } else if (d.status === 'permission_denied') toast.error('TikTok · video.list 권한이 필요합니다. 다시 연결해주세요.');
      else if (d.status === 'no_oauth')       toast.error('TikTok 계정을 먼저 연결해주세요.');
      else if (d.status === 'not_configured') toast.error('TikTok API 설정이 아직 완료되지 않았습니다.');
      else if (d.status === 'api_error')      toast.error(`TikTok API 오류: ${d.error_code || d.http_status || 'unknown'}`);
      else if (d.status === 'error')          toast.error(`TikTok 스캔 실패: ${d.error_code || 'unknown'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'TikTok 스캔 실패'); }
  };
  const scanIgNow = async () => {
    try {
      const r = await api.post('/seller/short-clips/instagram/scan');
      const d = r.data || {};
      if (d.status === 'scanned') {
        if (d.inserted > 0) toast.success(`Instagram 에서 새 Reels ${d.inserted}개를 가져왔습니다. 상품을 연결해주세요.`);
        else toast.info(`Instagram · 신규 Reels 가 없습니다.${d.total_media ? ` (전체 미디어 ${d.total_media}개 중 Reels 0)` : ''}`);
      } else if (d.status === 'no_oauth')       toast.error('Instagram 계정을 먼저 연결해주세요.');
      else if (d.status === 'not_configured') toast.error('Instagram API 설정이 아직 완료되지 않았습니다.');
      else if (d.status === 'api_error')      toast.error(`Instagram API 오류: ${d.http_status || d.error_code || 'unknown'}`);
      else if (d.status === 'error')          toast.error(`Instagram 스캔 실패: ${d.error_code || 'unknown'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Instagram 스캔 실패'); }
  };

  return (
    <div className="space-y-3" data-testid="shorts-autoscan-banner">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-black">쇼츠 자동 스캔</p>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-black ${ytConnected ? 'bg-red-600 text-white' : 'bg-gray-300 text-gray-700 dark:bg-white/10 dark:text-gray-300'}`}
          data-testid="autoscan-yt-badge">
          YouTube {ytConnected ? '연결됨' : '미연결'}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-black ${ttConnected ? 'bg-black text-white dark:bg-white dark:text-black' : 'bg-gray-300 text-gray-700 dark:bg-white/10 dark:text-gray-300'}`}
          data-testid="autoscan-tt-badge">
          TikTok {ttConnected ? '연결됨' : '미연결'}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-black ${igConnected ? 'bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white' : 'bg-gray-300 text-gray-700 dark:bg-white/10 dark:text-gray-300'}`}
          data-testid="autoscan-ig-badge">
          Instagram {igConnected ? '연결됨' : '미연결'}
        </span>
      </div>
      {!anyConnected && (
        <div className="p-3 rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] flex items-start gap-3" data-testid="autoscan-disconnected">
          <div className="w-9 h-9 rounded-lg bg-black/5 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
            <RefreshCw size={16} className="text-gray-500"/>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-gray-500 mt-0.5">
              YouTube / TikTok / Instagram 계정을 연결하면 신규 쇼츠·Reels 를 자동으로 가져옵니다. 판매자 라이브 컨트롤에서 계정을 연결하세요.
            </p>
          </div>
        </div>
      )}
      {ytConnected && (
        <PlatformScanRow platform="youtube" settings={yt} onScanned={onScanned}
          onToggle={toggleYt} onScanNow={scanYtNow}/>
      )}
      {ttConnected && (
        <PlatformScanRow platform="tiktok" settings={tt} onScanned={onScanned}
          onToggle={toggleTt} onScanNow={scanTtNow}/>
      )}
      {igConnected && (
        <PlatformScanRow platform="instagram" settings={ig} onScanned={onScanned}
          onToggle={toggleIg} onScanNow={scanIgNow}/>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Prod-S2.1 · 자동수집 신규 알림 배너
// ---------------------------------------------------------------------------
const AUTOSCAN_SEEN_KEY = 'livemoa_autoscan_last_seen';

function AutoscanNoticeBanner({ pendingClips, onDismiss }) {
  const [seenAt, setSeenAt] = useState(() => {
    try { return localStorage.getItem(AUTOSCAN_SEEN_KEY) || ''; } catch { return ''; }
  });
  // Prod-S · platform 별 신규 개수 분해. 기존 데이터(short_clips.platform)만 사용.
  const fresh = useMemo(() => {
    const seen = seenAt ? new Date(seenAt).getTime() : 0;
    const arr = pendingClips.filter((c) => {
      if (c.source_type !== 'auto_scan') return false;
      const t = c.created_at ? new Date(c.created_at).getTime() : 0;
      return t > seen;
    });
    const yt = arr.filter((c) => c.platform === 'youtube').length;
    const tt = arr.filter((c) => c.platform === 'tiktok').length;
    const ig = arr.filter((c) => c.platform === 'instagram').length;
    return { total: arr.length, yt, tt, ig };
  }, [pendingClips, seenAt]);

  if (fresh.total === 0) return null;
  const dismiss = () => {
    const now = new Date().toISOString();
    try { localStorage.setItem(AUTOSCAN_SEEN_KEY, now); } catch { /* ignore */ }
    setSeenAt(now);
    onDismiss?.();
  };
  // Message shape: 단일 플랫폼이면 그 이름 명시 · 혼합이면 breakdown 표기.
  let mainMsg;
  const platCount = [fresh.yt, fresh.tt, fresh.ig].filter((n) => n > 0).length;
  if (platCount === 1) {
    if (fresh.yt > 0) mainMsg = `YouTube 에서 새 쇼츠 ${fresh.yt}개를 가져왔습니다.`;
    else if (fresh.tt > 0) mainMsg = `TikTok 에서 새 쇼츠 ${fresh.tt}개를 가져왔습니다.`;
    else mainMsg = `Instagram 에서 새 Reels ${fresh.ig}개를 가져왔습니다.`;
  } else {
    mainMsg = `새 쇼츠 ${fresh.total}개를 가져왔습니다. · YouTube ${fresh.yt} · TikTok ${fresh.tt} · Instagram ${fresh.ig}`;
  }
  return (
    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/40 flex items-start gap-3" data-testid="autoscan-notice-banner">
      <div className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
        <Bell size={16}/>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-amber-800 dark:text-amber-200" data-testid="autoscan-notice-text">
          {mainMsg}
        </p>
        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
          아래에서 상품을 연결하면 바로 소비자 피드와 프로필에 공개됩니다.
        </p>
      </div>
      <button onClick={dismiss} className="p-1.5 rounded hover:bg-amber-100 dark:hover:bg-amber-500/20 text-amber-700 dark:text-amber-300"
        data-testid="autoscan-notice-dismiss">
        <X size={16}/>
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Prod-S3 · pending_link 카드 · 다중 상품 인라인 선택 + 연결
// ---------------------------------------------------------------------------
function PendingLinkCard({ clip, products, onLinked, onDelete }) {
  const [productIds, setProductIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (productIds.length === 0) { toast.error('연결할 상품을 최소 1개 선택해주세요.'); return; }
    setBusy(true);
    try {
      await api.patch(`/seller/short-clips/${clip.clip_id}`, { linked_products: productIds });
      toast.success(`상품 ${productIds.length}개가 연결되었습니다. 소비자 피드에 노출됩니다.`);
      setDone(true);
      setTimeout(() => onLinked?.(clip.clip_id), 500);
    } catch (e) { toast.error(e?.response?.data?.detail || '연결 실패'); }
    finally { setBusy(false); }
  };

  const publishedLabel = clip.published_at
    ? new Date(clip.published_at).toLocaleDateString('ko-KR')
    : null;

  return (
    <div className={`rounded-2xl border overflow-hidden bg-white dark:bg-[#141414] transition-all
        ${done ? 'border-emerald-400 dark:border-emerald-500/60 opacity-70' : 'border-amber-300 dark:border-amber-500/40'}`}
      data-testid={`pending-card-${clip.clip_id}`}>
      <div className="relative aspect-[9/16] bg-gray-100 dark:bg-white/5">
        {clip.thumbnail_url && <img src={clip.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
        <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
          {clip.platform === 'youtube' && <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-black">YouTube</span>}
          {clip.platform === 'tiktok'  && <span className="px-2 py-0.5 rounded-full bg-black text-white text-[10px] font-black">TikTok</span>}
          {clip.platform === 'instagram' && <span className="px-2 py-0.5 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white text-[10px] font-black">Instagram</span>}
          <span className="px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-black">자동수집</span>
          {done ? (
            <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black inline-flex items-center gap-0.5">
              <CheckCircle2 size={10}/> 연결 완료
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black inline-flex items-center gap-0.5"
              data-testid={`pending-badge-${clip.clip_id}`}>
              <AlertCircle size={10}/> 상품 연결 필요
            </span>
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          <p className="text-xs font-black truncate">{clip.title || '(제목 없음)'}</p>
          {publishedLabel && (
            <p className="text-[10px] opacity-80 mt-0.5">게시 · {publishedLabel}</p>
          )}
        </div>
      </div>
      <div className="p-3 space-y-2">
        <MultiProductPicker
          products={products}
          value={productIds}
          onChange={setProductIds}
          disabled={done || busy}
          testIdPrefix={`pending-mpp-${clip.clip_id}`}
        />
        <div className="flex gap-1.5">
          <button onClick={submit} disabled={done || busy || productIds.length === 0}
            className="flex-1 px-3 py-1.5 rounded-full text-[11px] font-black bg-[#FF3B30] text-white inline-flex items-center justify-center gap-1 disabled:opacity-40"
            data-testid={`pending-submit-${clip.clip_id}`}>
            {done ? (<><CheckCircle2 size={11}/> 완료</>) : (busy ? '연결 중…' : (<><Link2 size={11}/> 연결 ({productIds.length})</>))}
          </button>
          <button onClick={() => onDelete?.(clip.clip_id)} disabled={done || busy}
            className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 disabled:opacity-40"
            data-testid={`pending-del-${clip.clip_id}`}>
            <Trash2 size={12}/>
          </button>
        </div>
      </div>
    </div>
  );
}


export default function SellerShortClips() {
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // STEP 4-A · Short-form 순서변경 (Drag & Drop + 화살표) · SellerLiveControl 패턴 재사용.
  const [dragCid, setDragCid] = useState(null);
  const [dragOverCid, setDragOverCid] = useState(null);
  const [reordering, setReordering] = useState(false);
  // SHORT-FORM MGMT UX · 정렬 · 플랫폼 필터 · 상태 필터 (모두 프론트 필터링 · 기존 API REUSE).
  const [sortMode, setSortMode] = useState('newest'); // 'newest' | 'oldest'
  const [platformFilter, setPlatformFilter] = useState('all'); // 'all'|'livemoa'|'youtube'|'instagram'|'tiktok'
  const [statusFilter, setStatusFilter] = useState('all');     // 'all'|'success'|'in_progress'|'failed'
  // clip_id → publish_jobs 첫 항목 (없으면 null) · PublishStatusPills 이중 fetch 방지 + 필터 판정.
  const [jobMap, setJobMap] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get('/seller/short-clips');
      setClips(r.data?.items || []);
    } catch { /* silent */ }
  }, []);
  const loadProducts = useCallback(async () => {
    try {
      const r = await api.get('/seller/products');
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setProducts(arr);
    } catch { setProducts([]); }
  }, []);
  // SHORT-FORM MGMT UX · 활성 클립의 publish_jobs 병렬 fetch (기존 endpoint REUSE).
  //   · 자동수집 클립은 skip (원본 platform 은 clip.platform 으로 이미 판정 가능).
  //   · 실패한 클립은 null 로 표기해 필터에서 LIVEMOA 로 취급.
  const loadJobs = useCallback(async (clipList) => {
    const targets = (clipList || []).filter(
      (c) => c.status !== 'pending_link' && c.source_type !== 'auto_scan');
    if (targets.length === 0) { setJobMap({}); return; }
    const entries = await Promise.all(targets.map(async (c) => {
      try {
        const r = await api.get(`/seller/short-clips/${c.clip_id}/publish-jobs`);
        return [c.clip_id, (r.data?.items || [])[0] || null];
      } catch { return [c.clip_id, null]; }
    }));
    setJobMap(Object.fromEntries(entries));
  }, []);
  useEffect(() => { load(); loadProducts(); }, [load, loadProducts]);
  useEffect(() => { loadJobs(clips); }, [clips, loadJobs]);

  const del = async (cid) => {
    if (!window.confirm('이 숏클립을 삭제하시겠습니까?')) return;
    try { await api.delete(`/seller/short-clips/${cid}`); toast.success('삭제됨'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || '삭제 실패'); }
  };

  const handleLinked = (cid) => {
    setClips((prev) => prev.filter((c) => c.clip_id !== cid));
    setTimeout(load, 100);
  };

  // STEP 4-A · 순서 변경 helper — 화살표(위/아래) · Drag&Drop 공용 · POST /reorder 서버 저장.
  const persistOrder = async (nextIds) => {
    setReordering(true);
    try {
      await api.post('/seller/short-clips/reorder', { clip_ids: nextIds });
      toast.success('순서 저장됨');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '순서 저장 실패');
    } finally {
      setReordering(false);
    }
  };
  const moveClip = (cid, dir) => {
    const activeList = clips.filter((c) => c.status !== 'pending_link');
    // moveClip 은 서버 순서 조작 → 화면 필터/정렬과 무관하게 원본 순서 기준.
    const idx = activeList.findIndex((c) => c.clip_id === cid);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= activeList.length) return;
    const nextActive = [...activeList];
    [nextActive[idx], nextActive[j]] = [nextActive[j], nextActive[idx]];
    persistOrder(nextActive.map((c) => c.clip_id));
  };
  const onDropCard = (targetCid) => {
    if (!dragCid || dragCid === targetCid) return;
    const activeList = clips.filter((c) => c.status !== 'pending_link');
    const from = activeList.findIndex((c) => c.clip_id === dragCid);
    const to   = activeList.findIndex((c) => c.clip_id === targetCid);
    if (from < 0 || to < 0) return;
    const nextActive = [...activeList];
    const [moved] = nextActive.splice(from, 1);
    nextActive.splice(to, 0, moved);
    persistOrder(nextActive.map((c) => c.clip_id));
  };

  const pending = clips.filter((c) => c.status === 'pending_link');
  const activeAll = clips.filter((c) => c.status !== 'pending_link');

  // SHORT-FORM MGMT UX · 정렬 + 필터 (프론트 파생 · 기존 데이터 REUSE).
  const clipMatchesPlatform = (c) => {
    if (platformFilter === 'all') return true;
    if (platformFilter === 'livemoa') {
      // LIVEMOA-native = 자동수집(SNS Inbound)이 아닌 모든 클립.
      return c.source_type !== 'auto_scan';
    }
    // youtube / instagram / tiktok:
    //   1) Inbound 자동수집 클립의 원본 플랫폼과 일치
    //   2) Outbound publish_jobs 로 해당 플랫폼에 게시 성공한 클립
    if (c.platform === platformFilter) return true;
    const jj = jobMap[c.clip_id];
    const st = jj?.platforms?.[platformFilter]?.status;
    return st === 'success' || st === 'publishing' || st === 'pending';
  };
  const clipMatchesStatus = (c) => {
    if (statusFilter === 'all') return true;
    const jj = jobMap[c.clip_id];
    const perPlat = jj?.platforms || {};
    const statuses = Object.values(perPlat).map((v) => String(v?.status || '').toLowerCase());
    if (statusFilter === 'success') {
      // LIVEMOA 등록 즉시 공개된 클립(Outbound job 없음) 도 '게시 완료' 로 포함.
      if (statuses.length === 0) return true;
      return statuses.includes('success');
    }
    if (statusFilter === 'in_progress') {
      return statuses.some((s) => s === 'pending' || s === 'publishing');
    }
    if (statusFilter === 'failed') {
      return statuses.some((s) => s === 'failed' || s === 'blocked');
    }
    return true;
  };
  const activeFiltered = activeAll.filter((c) => clipMatchesPlatform(c) && clipMatchesStatus(c));
  const activeSorted = [...activeFiltered].sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return sortMode === 'oldest' ? (ta - tb) : (tb - ta);
  });
  // 순서변경 API 는 기존 서버 순서를 조작하므로 sort 가 '최신순' 이 아닐 때는
  // 드래그/화살표 UX 를 비활성 · 사용자 혼동 방지.
  const reorderEnabled = sortMode === 'newest'
    && platformFilter === 'all' && statusFilter === 'all';
  const active = activeSorted;

  const renderCard = (c) => {
    // Prod-S3 · linked_products 우선. legacy 는 서버가 이미 자동 변환해 배열로 내려줌.
    const linked = Array.isArray(c.linked_products) ? c.linked_products : [];
    const primary = linked[0]?.product_snapshot || c.product_snapshot;
    const extraCount = Math.max(0, linked.length - 1);
    return (
      <div key={c.clip_id} className="rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden" data-testid={`clip-card-${c.clip_id}`}>
        <div className="relative aspect-[9/16] bg-gray-100 dark:bg-white/5">
          {c.thumbnail_url ? (
            <img src={c.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover"/>
          ) : (c.source_media_id && typeof c.video_url === 'string' && c.video_url.includes('/api/seller/short-clip-media/')) ? (
            <DirectUploadVideoPreview videoUrl={c.video_url}/>
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"/>
          <div className="absolute top-2 left-2 flex gap-1 flex-wrap">
            {c.platform === 'youtube' && <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-black">YouTube</span>}
            {c.platform === 'tiktok' && <span className="px-2 py-0.5 rounded-full bg-black text-white text-[10px] font-black">TikTok</span>}
            {c.platform === 'instagram' && <span className="px-2 py-0.5 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white text-[10px] font-black">Instagram</span>}
            {c.source_type === 'auto_scan' && <span className="px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-black">자동수집</span>}
            {c.is_featured && <span className="px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black inline-flex items-center gap-0.5"><Star size={10}/> 추천</span>}
            {extraCount > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-white text-black text-[10px] font-black inline-flex items-center gap-0.5"
                data-testid={`clip-multi-badge-${c.clip_id}`}>
                <Package size={10}/> +{extraCount}
              </span>
            )}
          </div>
          <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center"><Play size={14} className="text-black ml-0.5"/></div>
          <div className="absolute inset-x-0 bottom-0 p-3 text-white">
            {primary ? (
              <>
                <p className="text-xs font-bold truncate">{primary?.name}</p>
                <p className="text-sm font-black">{won(primary?.sale_price)}</p>
              </>
            ) : (
              <p className="text-xs font-bold truncate opacity-80">{c.title || '(제목 없음)'}</p>
            )}
          </div>
        </div>
        <div className="p-3 flex items-center gap-2 text-xs">
          <span className="text-gray-500" title="LIVEMOA 앱 내부 노출 (외부 플랫폼 조회수 아님)">👁 LIVEMOA 노출 {c.view_count}</span>
          {linked.length > 1 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10 font-black">
              상품 {linked.length}개
            </span>
          )}
          <div className="ml-auto flex gap-1">
            {/* STEP SOCIAL-COMMENT-UI-1 · 해당 숏폼의 Instagram/TikTok 댓글로 이동 */}
            <Link to={`/seller/comments?source_clip_id=${encodeURIComponent(c.clip_id)}`}
                  className="px-2 py-1 rounded-full text-[10px] font-bold border border-black/10 inline-flex items-center gap-1 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  data-testid={`clip-comments-${c.clip_id}`}>
              <MessageCircle size={10}/> 댓글 보기
            </Link>
            <button onClick={() => { setEditing(c); setOpen(true); }} className="px-2 py-1 rounded-full text-[10px] font-bold border border-black/10" data-testid={`clip-edit-${c.clip_id}`}>수정</button>
            <button onClick={() => del(c.clip_id)} className="p-1 rounded text-red-500 hover:bg-red-50" data-testid={`clip-del-${c.clip_id}`}><Trash2 size={12}/></button>
          </div>
        </div>
        <div className="px-3 pb-3">
          <PublishStatusPills clipId={c.clip_id} sourceType={c.source_type}
            job={c.source_type === 'auto_scan' ? undefined : (jobMap[c.clip_id] ?? null)}
            onRetry={() => loadJobs(clips)}/>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="seller-short-clips-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black">
        <ArrowLeft size={14}/> 뒤로
      </button>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>🎬 숏클립 관리</h1>
            {pending.length > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-black bg-amber-500 text-white inline-flex items-center gap-1"
                data-testid="pending-link-count-badge">
                <AlertCircle size={11}/> 상품 연결 대기 {pending.length}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">상품 숏폼 등록 · 최대 {MAX_LINKED_PRODUCTS}개 상품 연결 · 등록 즉시 공개</p>
        </div>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="px-4 py-2 rounded-full text-sm font-black bg-[#FF3B30] text-white inline-flex items-center gap-1" data-testid="clip-new-btn">
          <Plus size={14}/> 새 클립 등록
        </button>
      </div>

      <AutoscanNoticeBanner pendingClips={pending} onDismiss={() => { /* stateless dismiss */ }}/>

      <ShortsAutoScanBanner onScanned={load}/>

      {pending.length > 0 && (
        <div data-testid="pending-link-section">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} className="text-amber-500"/>
            <p className="text-xs font-black text-amber-600 dark:text-amber-400">
              상품 연결 대기 ({pending.length}) · 자동 스캔으로 가져온 Shorts 입니다
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pending-link-grid">
            {pending.map((c) => (
              <PendingLinkCard key={c.clip_id} clip={c} products={products}
                onLinked={handleLinked} onDelete={del}/>
            ))}
          </div>
        </div>
      )}

      {active.length === 0 && pending.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-16">등록된 클립이 없습니다.</p>
      ) : activeAll.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-black text-gray-500" data-testid="active-count-label">
              공개 중 ({activeSorted.length}{activeAll.length !== activeSorted.length ? ` / ${activeAll.length}` : ''})
            </p>
            <select value={sortMode} onChange={(e) => setSortMode(e.target.value)}
              className="px-2 py-1 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-[11px] font-black"
              data-testid="active-sort-select"
              aria-label="정렬 방식">
              <option value="newest">최신순</option>
              <option value="oldest">오래된순</option>
            </select>
          </div>

          {/* SHORT-FORM MGMT UX · 플랫폼 · 상태 필터 (mobile-first · 가로 chip + compact select) */}
          <div className="mb-3 space-y-2">
            <div className="-mx-4 sm:-mx-6 px-4 sm:px-6 overflow-x-auto scrollbar-hide"
              data-testid="active-platform-filter-bar">
              <div className="inline-flex gap-1.5 py-0.5">
                {[
                  { k: 'all',       lb: '전체',    Icon: null },
                  { k: 'livemoa',   lb: 'LIVEMOA', Icon: null },
                  { k: 'youtube',   lb: 'YouTube', Icon: Youtube },
                  { k: 'instagram', lb: 'Instagram', Icon: Instagram },
                  { k: 'tiktok',    lb: 'TikTok',  Icon: Music },
                ].map(({ k, lb, Icon }) => {
                  const on = platformFilter === k;
                  return (
                    <button key={k}
                      onClick={() => setPlatformFilter(k)}
                      className={`inline-flex items-center gap-1 whitespace-nowrap px-3 py-1.5 rounded-full text-[11px] font-black border transition
                        ${on
                          ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                          : 'bg-white dark:bg-[#141414] text-gray-700 dark:text-gray-200 border-black/10 dark:border-white/10 hover:border-[#FF3B30]/50'}`}
                      data-testid={`active-platform-filter-${k}`}>
                      {Icon ? <Icon size={11}/> : null}
                      {lb}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-[10px] font-black text-gray-500">상태</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1 rounded-full border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-[11px] font-black"
                data-testid="active-status-select"
                aria-label="게시 상태 필터">
                <option value="all">전체 상태</option>
                <option value="success">게시 완료</option>
                <option value="in_progress">게시 중/대기</option>
                <option value="failed">게시 실패/불가</option>
              </select>
              {(platformFilter !== 'all' || statusFilter !== 'all' || sortMode !== 'newest') && (
                <button onClick={() => { setPlatformFilter('all'); setStatusFilter('all'); setSortMode('newest'); }}
                  className="ml-auto text-[10px] font-bold text-gray-500 underline"
                  data-testid="active-filter-reset">필터 초기화</button>
              )}
            </div>
          </div>

          {reorderEnabled ? (
            <p className="text-[11px] text-gray-500 mb-3">▲▼ 버튼 또는 카드를 끌어놓으면 판매자 프로필에 표시되는 순서를 바꿀 수 있습니다.</p>
          ) : (
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-3">※ 필터/정렬 적용 중 · 순서 변경은 [최신순 · 전체 · 전체 상태] 에서만 가능합니다.</p>
          )}
          {active.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-8"
              data-testid="active-empty-filter">조건에 맞는 클립이 없습니다.</p>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {active.map((c, i) => (
              <div key={c.clip_id}
                draggable={reorderEnabled}
                onDragStart={(e) => { if (!reorderEnabled) return; setDragCid(c.clip_id); try { e.dataTransfer.effectAllowed = 'move'; } catch {} }}
                onDragOver={(e) => { if (!reorderEnabled) return; e.preventDefault(); setDragOverCid(c.clip_id); }}
                onDragLeave={() => setDragOverCid(null)}
                onDrop={(e) => { if (!reorderEnabled) return; e.preventDefault(); onDropCard(c.clip_id); }}
                onDragEnd={() => { setDragCid(null); setDragOverCid(null); }}
                className={`relative ${dragOverCid === c.clip_id ? 'ring-2 ring-[#FF3B30]' : ''} ${dragCid === c.clip_id ? 'opacity-40' : ''}`}
                data-testid={`clip-reorder-wrap-${c.clip_id}`}>
                {reorderEnabled && (
                  <div className="absolute top-2 right-2 z-10 flex gap-1 bg-white/95 dark:bg-black/70 rounded-full p-0.5 shadow">
                    <button disabled={reordering || i === 0} onClick={() => moveClip(c.clip_id, -1)}
                      className="p-1 rounded-full text-xs disabled:opacity-30 hover:bg-black/5"
                      data-testid={`clip-up-${c.clip_id}`} aria-label="위로"><ArrowUp size={12}/></button>
                    <button disabled={reordering || i === active.length - 1} onClick={() => moveClip(c.clip_id, 1)}
                      className="p-1 rounded-full text-xs disabled:opacity-30 hover:bg-black/5"
                      data-testid={`clip-down-${c.clip_id}`} aria-label="아래로"><ArrowDown size={12}/></button>
                  </div>
                )}
                {renderCard(c)}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      <ClipModal open={open} onClose={() => setOpen(false)} onSaved={load} editing={editing}/>
    </div>
  );
}
